/**
 * Shim for next/navigation that provides compatible navigation hooks for non-Next.js environments.
 * Uses browser's native navigation APIs.
 */

/**
 * Returns an object with navigation methods.
 * Shim for Next.js useRouter hook.
 */
export function useRouter() {
    return {
        push: (url: string) => {
            window.location.href = url;
        },
        replace: (url: string) => {
            window.location.replace(url);
        },
        back: () => {
            window.history.back();
        },
        forward: () => {
            window.history.forward();
        },
        refresh: () => {
            window.location.reload();
        },
        prefetch: (url: string) => {
            // No-op in non-Next.js environment
            void url;
        },
    };
}

/**
 * Returns the current pathname.
 * Shim for Next.js usePathname hook.
 */
export function usePathname(): string {
    if (typeof window === 'undefined') {
        return '/';
    }
    return window.location.pathname;
}

/**
 * Returns the current search params.
 * Shim for Next.js useSearchParams hook.
 */
export function useSearchParams() {
    if (typeof window === 'undefined') {
        return new URLSearchParams();
    }
    return new URLSearchParams(window.location.search);
}

/**
 * Returns the current params from the URL.
 * Shim for Next.js useParams hook.
 */
export function useParams(): Record<string, string | string[]> {
    // In a non-Next.js environment, we can't easily extract dynamic route params
    // Return an empty object as a fallback
    return {};
}

/**
 * Programmatic navigation function.
 * Shim for Next.js redirect function.
 */
export function redirect(url: string): never {
    if (typeof window !== 'undefined') {
        window.location.href = url;
    }
    // This throw is to satisfy TypeScript's never return type
    throw new Error(`Redirect to ${url}`);
}

/**
 * Shim for Next.js notFound function.
 */
export function notFound(): never {
    throw new Error('Page not found');
}

/**
 * Returns the selected layout segment.
 * Shim for Next.js useSelectedLayoutSegment hook.
 */
export function useSelectedLayoutSegment(): string | null {
    return null;
}

/**
 * Returns all selected layout segments.
 * Shim for Next.js useSelectedLayoutSegments hook.
 */
export function useSelectedLayoutSegments(): string[] {
    return [];
}
